<template>
  <transition name="scm-fade">
    <div v-if="show" class="scm-overlay" @click.self="onCancel">
      <div class="scm-modal md-elevation-10">
        <div class="scm-header">
          <span class="scm-title">{{ isEdit ? (T.edit_title || 'Edit salutation') : (T.modal_title || 'New salutation') }}</span>
          <span class="scm-lang">{{ langName }}</span>
        </div>

        <div class="scm-body">
          <div class="scm-field">
            <label class="scm-label">{{ T.choice_label || 'Label' }}</label>
            <input
                ref="labelInput"
                class="scm-input"
                type="text"
                :placeholder="T.ph_label || ''"
                autocomplete="off"
                v-model="form.label" />
          </div>
          <div class="scm-field">
            <label class="scm-label">{{ T.male || 'Male' }}</label>
            <input class="scm-input" type="text" :placeholder="T.ph_male || ''" autocomplete="off" v-model="form.male" />
          </div>
          <div class="scm-field">
            <label class="scm-label">{{ T.female || 'Female' }}</label>
            <input class="scm-input" type="text" :placeholder="T.ph_female || ''" autocomplete="off" v-model="form.female" />
          </div>
          <div class="scm-field">
            <label class="scm-label">{{ T.neutral || 'Neutral' }}</label>
            <input class="scm-input" type="text" :placeholder="T.ph_neutral || ''" autocomplete="off"
                   v-model="form.neutral" @keyup.enter="onSave" />
          </div>

          <p v-if="errorText" class="scm-error">{{ errorText }}</p>
        </div>

        <div class="scm-actions">
          <button class="scm-btn scm-btn--secondary" type="button" @click="onCancel">
            {{ L.close_window || 'Cancel' }}
          </button>
          <button class="scm-btn scm-btn--primary" type="button" @click="onSave">
            {{ L.Speichern || 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * Modal to add a custom salutation choice for a branding language.
 *
 * The choice defines what the [title] placeholder expands to (per gender) in the
 * branding texts of the language shown in the header — e.g. label "Liebe Anrede"
 * with male "lieber" / female "liebe" / neutral "liebe/r".
 *
 * Props:
 *  - show: visibility toggle
 *  - options: global options (needs options.Lang for translations)
 *  - langCode: the branding language being edited (shown in the header)
 *  - choice: existing choice to edit (prefills the form); null = add mode
 *
 * Emits:
 *  - 'save' ({ label, male, female, neutral }) — parent stores the choice
 *    (in edit mode the parent keeps the choice's id and updates it in place)
 *  - 'cancel' when closed without saving
 */
export default {
  name: 'SalutationChoiceModal',
  props: {
    show: { type: Boolean, default: false },
    options: { type: Object, required: true },
    langCode: { type: String, required: true },
    choice: { type: Object, default: null }
  },
  data() {
    return {
      form: { label: '', male: '', female: '', neutral: '' },
      errorText: ''
    };
  },
  computed: {
    L() { return this.options.Lang || {}; },
    T() { return this.L.salutation_select || {}; },
    // Localized name of the language this choice belongs to (e.g. "Deutsch").
    langName() {
      const map = this.L.lang_code || {};
      return map[this.langCode] || this.langCode;
    },
    isEdit() {
      return !!this.choice;
    }
  },
  // vue-material's .md-app-container has transform:translate3D(0,0,0), which traps
  // position:fixed descendants (the overlay becomes relative to the container and
  // can never cover the drawer/sidebar). Portal the modal element to <body> so the
  // overlay truly spans the viewport — same net effect as the App-root globalConfirm.
  mounted() {
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  watch: {
    show(open) {
      if (open) {
        // Prefill from the choice being edited; blank form in add mode.
        this.form = {
          label: this.choice?.label || '',
          male: this.choice?.male || '',
          female: this.choice?.female || '',
          neutral: this.choice?.neutral || ''
        };
        this.errorText = '';
        this.$nextTick(() => { if (this.$refs.labelInput) this.$refs.labelInput.focus(); });
      }
    }
  },
  methods: {
    onCancel() {
      this.$emit('cancel');
    },
    onSave() {
      const f = this.form;
      // Explicit validation messages instead of a silently disabled button.
      if (!f.label.trim()) {
        this.errorText = this.T.err_label_required || 'Please enter a label.';
        this.$nextTick(() => { if (this.$refs.labelInput) this.$refs.labelInput.focus(); });
        return;
      }
      if (!f.male.trim() || !f.female.trim() || !f.neutral.trim()) {
        this.errorText = this.T.err_fields_required || 'Please fill in all salutation fields (male, female, neutral).';
        return;
      }
      this.errorText = '';
      this.$emit('save', {
        label: f.label.trim(),
        male: f.male.trim(),
        female: f.female.trim(),
        neutral: f.neutral.trim()
      });
    }
  }
};
</script>

<style scoped>
.scm-overlay {
  position: fixed; inset: 0;
  /* Must beat the left sidebar (z-index 111111111) — same pattern as
     #globalConfirmOverlay in App.vue. */
  z-index: 100000000001;
  background: rgba(20, 24, 33, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.scm-modal {
  width: 440px; max-width: 100%;
  background: #fff; border-radius: 14px; overflow: hidden;
  display: flex; flex-direction: column;
}
.scm-header {
  display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
  padding: 1.5rem 1.75rem 0.5rem;
}
.scm-title { font-size: 1.2rem; font-weight: 700; color: #2c2c2c; }
.scm-lang {
  flex-shrink: 0;
  font-size: 0.8rem; font-weight: 600; color: #4a5568;
  background: #eef1f5; border-radius: 999px; padding: 0.25rem 0.7rem;
}
.scm-body { padding: 0.5rem 1.75rem 1.25rem; }
.scm-field { margin-bottom: 0.85rem; }
.scm-label { display: block; font-size: 0.78rem; font-weight: 600; color: #555; margin-bottom: 0.3rem; }
.scm-input {
  width: 100%; height: 44px; border: 1px solid #dcdcdc; border-radius: 8px;
  background: #fafafa; padding: 0 0.85rem; font-size: 0.9rem; color: #202224;
  box-sizing: border-box; transition: border-color .15s ease, background .15s ease;
}
.scm-input:focus { outline: none; border-color: #999; background: #fff; }

.scm-error { color: #c0392b; font-size: 0.82rem; margin: 0.2rem 0 0; line-height: 1.4; }

.scm-actions {
  display: flex; justify-content: flex-end; gap: 0.75rem;
  padding: 0.75rem 1.75rem 1.5rem;
}
.scm-btn {
  border: none; border-radius: 8px; padding: 0.6rem 1.1rem;
  font-size: 0.88rem; font-weight: 600; cursor: pointer; min-width: 110px;
}
.scm-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.scm-btn--primary { background: #343841; color: #fff; }
.scm-btn--secondary { background: #eef1f5; color: #374151; }

.scm-fade-enter-active, .scm-fade-leave-active { transition: opacity .2s ease; }
.scm-fade-enter, .scm-fade-leave-to { opacity: 0; }
.scm-fade-enter-active .scm-modal { animation: scm-pop .22s ease; }
@keyframes scm-pop { from { transform: translateY(8px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
</style>
